/**
 * EstimateForm (Phase D1.1-B · Feb 2026)
 * --------------------------------------
 * Create / edit an Estimate. Reuses the existing catalog engine
 * (AddProductRouter → CatalogPicker → CatalogProductDetail) so products,
 * prices and commercial engine (Fase C) all behave EXACTLY as in Orders —
 * strictly additive, no logic duplication.
 *
 * Contract:
 *   props.initial   → hydration payload from GET /api/commercial/estimates/{id}
 *   props.onCancel  → close
 *   props.onSaved   → callback(estimate) after successful create/update
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { computeOrderCommercialTotals, calculateSellingPrice, isBeanie, isLongSleeve, MIX_POOL_MIN, MIX_POOL_LABELS } from "@/lib/commercialEngine";
import VinylCalculatorModal from "@/components/VinylCalculatorModal";
import BannerCalculatorModal from "@/components/BannerCalculatorModal";
import AddProductRouter from "@/components/catalog_engine/AddProductRouter";
import CatalogPicker from "@/components/CatalogPicker";
import ServiceItemForm from "@/components/ServiceItemForm";
import CustomProductEditor from "@/components/CustomProductEditor";
import ProductCatalogPickerModal from "@/pages/product_catalog/ProductCatalogPickerModal";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const initialForm = (initial, settings) => ({
  office_key: initial?.office_key || settings?.default_office_key || "murfreesboro",
  customer_id: initial?.customer_id || null,
  company: initial?.company || "",
  contact_person: initial?.contact_person || "",
  customer_phone: initial?.customer_phone || "",
  customer_email: initial?.customer_email || "",
  delivery_date: initial?.delivery_date || "",
  items: (initial?.items || []).map((it) => ({ ...it })),
  discount_type: initial?.discount_type || "amount",
  discount_value: initial?.discount_value || 0,
  tax_exempt: !!initial?.tax_exempt,
  sales_tax_rate: initial?.sales_tax_rate ?? 0.0975,
  deposit_received: initial?.deposit_received || 0,
  customer_notes: initial?.customer_notes || "",
  internal_notes: initial?.internal_notes || "",
  follow_up_date: initial?.follow_up_date || "",
  follow_up_notes: initial?.follow_up_notes || "",
});

export default function EstimateForm({ initial, onCancel, onSaved }) {
  const { user, isPartner, isAdmin } = useAuth();
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(() => initialForm(initial, null));
  const [saving, setSaving] = useState(false);

  // Add-product flow
  const [pickerOpen, setPickerOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  // Sprint · Feb 2026 — Modal read-only de consulta del Product Catalog interno.
  const [customCatalogOpen, setCustomCatalogOpen] = useState(false);
  const [vinylOpen, setVinylOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  // FASE 2 · Jun 2026 — Producto Manual (línea ad-hoc, no se guarda en catálogo)
  const [manualOpen, setManualOpen] = useState(false);
  // Custom Product editor (blank when index=null, edit when index=number)
  const [customEditor, setCustomEditor] = useState({ open: false, index: null });

  // Customer dedupe lookup (debounced)
  const [customerHints, setCustomerHints] = useState([]);
  const [lookupTimer, setLookupTimer] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/commercial/settings`, {
          withCredentials: true,
        });
        setSettings(data);
        if (!initial) {
          setForm((f) => ({
            ...f,
            office_key: f.office_key || data.default_office_key || "murfreesboro",
          }));
        }
      } catch {
        /* non-blocking */
      }
    })();
  }, [initial]);

  // Fase C recompute — mirrors what backend does on save.
  const totals = useMemo(() => {
    const subtotal = (form.items || []).reduce(
      (acc, it) =>
        acc + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0),
      0
    );
    const t = computeOrderCommercialTotals({
      subtotal,
      discountType: form.discount_type,
      discountValue: form.discount_value,
      taxExempt: form.tax_exempt,
      salesTaxRate: form.sales_tax_rate,
      depositReceived: form.deposit_received,
    });
    return { subtotal, ...t };
  }, [form]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addItem = (item) => {
    // AddProductRouter/CatalogProductDetail may return an array or single item.
    const list = Array.isArray(item) ? item : [item];
    setForm((f) => ({ ...f, items: [...f.items, ...list] }));
  };
  const updateItem = (idx, patch) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // MIX & MATCH (Jun 2026) — pools independientes (TSHIRTS/POLOS/HOODIES/
  // JACKETS/CAPS_EMBROIDERY/CAPS_LEATHER/BEANIES): el TOTAL combinado del pool
  // valida el mínimo y selecciona el tier; cada SKU conserva su propio costo.
  // Las líneas con precio manual del admin (price_manual) NO se recalculan.
  // Items antiguos sin mix_pool: solo Beanies participa (por nombre, legacy).
  const itemPool = (it) =>
    it.mix_pool || (isBeanie(it.description) ? "BEANIES" : null);
  const poolTotals = useMemo(() => {
    const totals = {};
    for (const it of form.items || []) {
      const p = itemPool(it);
      if (!p) continue;
      totals[p] = (totals[p] || 0) + (Number(it.quantity) || 0);
    }
    return totals;
  }, [form.items]);
  const poolsBelowMin = useMemo(
    () =>
      Object.entries(poolTotals)
        .filter(([p, tq]) => tq > 0 && tq < MIX_POOL_MIN[p])
        .map(([p, tq]) => ({
          pool: p,
          total: tq,
          min: MIX_POOL_MIN[p],
          label: MIX_POOL_LABELS[p],
        })),
    [poolTotals]
  );
  useEffect(() => {
    // Bloque 1 Partner Points — los partners no tienen costos en el navegador;
    // su re-precio por pool corre contra el backend (efecto siguiente).
    if (isPartner) return;
    const ready = Object.entries(poolTotals).some(
      ([p, tq]) => tq >= MIX_POOL_MIN[p]
    );
    if (!ready) return;
    setForm((f) => {
      let changed = false;
      const items = f.items.map((it) => {
        const p = itemPool(it);
        if (!p || it.price_manual) return it;
        const tq = poolTotals[p] || 0;
        // Excepción ADMIN (Jun 2026): bajo el mínimo precia con el primer tier
        // (combinedQty clampeado al mínimo). Empleados: comportamiento intacto.
        const tqEff = isAdmin ? Math.max(tq, MIX_POOL_MIN[p]) : tq;
        if (tqEff < MIX_POOL_MIN[p]) return it;
        const group = it.commercial_group || (p === "BEANIES" ? "HATS" : null);
        if (!group) return it;
        const q = calculateSellingPrice({
          group,
          qty: Number(it.quantity) || 0,
          baseCost: Number(it.unit_cost) || 0,
          longSleeve: isLongSleeve(it.description, ""),
          size: it.catalog_size || "",
          decorationMethod: p === "CAPS_LEATHER" ? "Leather Patch" : "",
          beanie: p === "BEANIES",
          combinedQty: tqEff,
          polo: p === "POLOS",
        });
        if (!q.ok || Math.abs((Number(it.unit_price) || 0) - q.unit_price) < 0.005)
          return it;
        changed = true;
        return { ...it, unit_price: q.unit_price };
      });
      return changed ? { ...f, items } : f;
    });
  }, [poolTotals]);

  // Bloque 1 Partner Points — re-precio Mix & Match vía backend (quote-item
  // con style): el partner nunca calcula con costos locales.
  useEffect(() => {
    if (!isPartner) return;
    const ready = Object.entries(poolTotals).some(
      ([p, tq]) => tq >= MIX_POOL_MIN[p]
    );
    if (!ready) return;
    let cancelled = false;
    (async () => {
      const lines = (form.items || [])
        .map((it, idx) => ({ it, idx, pool: itemPool(it) }))
        .filter(
          ({ it, pool }) =>
            pool &&
            !it.price_manual &&
            it.catalog_style &&
            it.commercial_group &&
            (poolTotals[pool] || 0) >= MIX_POOL_MIN[pool]
        );
      if (!lines.length) return;
      const results = await Promise.all(
        lines.map(({ it, pool }) =>
          axios
            .post(
              `${API}/commercial/quote-item`,
              {
                group: it.commercial_group,
                qty: Number(it.quantity) || 0,
                style: it.catalog_style,
                size: it.catalog_size || "",
                long_sleeve: isLongSleeve(it.description, ""),
                decoration_method: pool === "CAPS_LEATHER" ? "Leather Patch" : null,
                beanie: pool === "BEANIES",
                combined_qty: poolTotals[pool],
                polo: pool === "POLOS",
              },
              { withCredentials: true }
            )
            .then((r) => r.data)
            .catch(() => null)
        )
      );
      if (cancelled) return;
      const priceByIdx = {};
      lines.forEach(({ idx }, i) => {
        const q = results[i];
        if (q?.ok) priceByIdx[idx] = q.unit_price;
      });
      if (!Object.keys(priceByIdx).length) return;
      setForm((f) => {
        let changed = false;
        const items = f.items.map((it, idx) => {
          const np = priceByIdx[idx];
          if (np == null || Math.abs((Number(it.unit_price) || 0) - np) < 0.005)
            return it;
          changed = true;
          return { ...it, unit_price: np };
        });
        return changed ? { ...f, items } : f;
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartner, poolTotals]);

  // Dedupe lookup on company/email/phone blur
  const runLookup = async () => {
    if (!form.company?.trim()) return;
    try {
      const { data } = await axios.post(
        `${API}/commercial/customers/lookup`,
        {
          company: form.company,
          email: form.customer_email,
          phone: form.customer_phone,
        },
        { withCredentials: true }
      );
      if (data && data.id && data.id !== form.customer_id) {
        setCustomerHints([data]);
      } else {
        setCustomerHints([]);
      }
    } catch {
      /* silent */
    }
  };

  const scheduleLookup = () => {
    if (lookupTimer) clearTimeout(lookupTimer);
    setLookupTimer(setTimeout(runLookup, 350));
  };

  const applyCustomerHint = (c) => {
    setForm((f) => ({
      ...f,
      customer_id: c.id,
      company: c.company || f.company,
      contact_person: c.contact_person || f.contact_person,
      customer_phone: c.phone || f.customer_phone,
      customer_email: c.email || f.customer_email,
    }));
    setCustomerHints([]);
  };

  const submit = async () => {
    if (!form.company.trim()) {
      toast.error(t("est_form_required_company"));
      return;
    }
    if (!form.office_key) {
      toast.error(t("est_form_required_office"));
      return;
    }
    if (form.items.length === 0) {
      toast.error(t("est_form_required_items"));
      return;
    }
    // MIX & MATCH — mínimo combinado por pool validado al guardar.
    // Excepción ADMIN (Jun 2026): puede guardar bajo el mínimo (primer tier).
    if (poolsBelowMin.length > 0 && !isAdmin) {
      const w = poolsBelowMin[0];
      toast.error(
        w.pool === "BEANIES"
          ? t("est_form_beanies_min")
          : t("est_form_pool_min", { pool: w.label, min: w.min })
      );
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      const url = initial?.id
        ? `${API}/commercial/estimates/${initial.id}`
        : `${API}/commercial/estimates`;
      const method = initial?.id ? "put" : "post";
      const { data } = await axios[method](url, payload, {
        withCredentials: true,
      });
      toast.success(initial?.id ? t("est_form_updated_ok") : t("est_form_created_ok", { n: data.number }));
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("est_form_save_err"));
    } finally {
      setSaving(false);
    }
  };

  const offices = settings?.offices || [];

  return (
    <div data-testid="estimate-form" className="p-6 space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="label-overline">EST / {initial?.number || "NEW"}</div>
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
            {initial?.id ? t("est_form_title_edit", { n: initial.number }) : t("est_form_title_new")}
          </h2>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            {t("est_form_sales_rep_hint", { name: user?.name || user?.identifier || "" })}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
          data-testid="estimate-form-cancel"
        >
          {t("cancel")}
        </button>
      </header>

      {/* Office + delivery */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label={t("est_form_field_office")}>
          <select
            data-testid="estimate-form-office"
            value={form.office_key}
            onChange={(e) => setField("office_key", e.target.value)}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          >
            {offices.map((o) => (
              <option key={o.key} value={o.key}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("est_form_field_delivery")}>
          <input
            data-testid="estimate-form-delivery-date"
            type="date"
            value={form.delivery_date || ""}
            onChange={(e) => setField("delivery_date", e.target.value)}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
        <Field label={t("est_form_field_followup")}>
          <input
            data-testid="estimate-form-follow-up"
            type="date"
            value={form.follow_up_date || ""}
            onChange={(e) => setField("follow_up_date", e.target.value)}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
      </section>

      {/* Customer */}
      <section className="space-y-3">
        <div className="label-overline text-[var(--text-secondary)]">{t("est_form_section_customer")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("est_form_field_company")}>
            <input
              data-testid="estimate-form-company"
              value={form.company}
              onChange={(e) => {
                setField("company", e.target.value);
                setField("customer_id", null);
                scheduleLookup();
              }}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
            />
          </Field>
          <Field label={t("est_form_field_contact")}>
            <input
              data-testid="estimate-form-contact"
              value={form.contact_person}
              onChange={(e) => setField("contact_person", e.target.value)}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
            />
          </Field>
          <Field label={t("est_form_field_phone")}>
            <input
              data-testid="estimate-form-phone"
              value={form.customer_phone}
              onChange={(e) => {
                setField("customer_phone", e.target.value);
                scheduleLookup();
              }}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
            />
          </Field>
          <Field label={t("est_form_field_email")}>
            <input
              data-testid="estimate-form-email"
              type="email"
              value={form.customer_email}
              onChange={(e) => {
                setField("customer_email", e.target.value);
                scheduleLookup();
              }}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
            />
          </Field>
        </div>
        {customerHints.length > 0 && (
          <div
            data-testid="estimate-form-customer-hint"
            className="border border-[var(--accent-cyan,#00A6CE)] bg-cyan-50 p-3 text-sm"
          >
            <div className="font-mono-tech text-[0.65rem] font-bold tracking-wider text-[var(--text-secondary)] mb-1">
              {t("est_form_customer_hint_title")}
            </div>
            {customerHints.map((c) => (
              <div key={c.id} className="flex items-center justify-between">
                <div>
                  <b>{c.company}</b>
                  {c.contact_person ? ` · ${c.contact_person}` : ""}
                  <div className="text-xs text-[var(--text-secondary)]">
                    {c.phone} · {c.email}
                  </div>
                </div>
                <button
                  data-testid={`estimate-form-customer-hint-apply-${c.id}`}
                  onClick={() => applyCustomerHint(c)}
                  className="font-mono-tech text-xs border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
                >
                  {t("est_form_customer_hint_apply")}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Items */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="label-overline text-[var(--text-secondary)]">{t("est_form_section_items")}</div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Feb 2026 — botón "Catálogo" oculto de la UI (el selector de
                "+ Agregar producto" ya ofrece Ropa y Uniformes). El modal
                CatalogPicker y setCatalogOpen siguen en uso por ese flujo. */}
            {false && (
              <button
                type="button"
                data-testid="estimate-form-open-catalog"
                onClick={() => setCatalogOpen(true)}
                className="font-mono-tech text-xs border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
              >
                {t("est_form_open_catalog_btn")}
              </button>
            )}
            <button
              type="button"
              data-testid="estimate-form-add-item"
              onClick={() => setPickerOpen(true)}
              className="font-mono-tech text-xs font-bold bg-black text-white border border-black px-3 py-1.5 hover:bg-[var(--primary)] hover:border-[var(--primary)] transition-colors"
            >
              {t("est_form_add_item")}
            </button>
          </div>
        </div>
        {poolsBelowMin.map((w) => (
          <div
            key={w.pool}
            data-testid={
              w.pool === "BEANIES"
                ? "estimate-form-beanies-warning"
                : `estimate-form-pool-warning-${w.pool}`
            }
            className="border-l-4 border-[#B8860B] bg-[#FFF8E1] px-3 py-2 text-xs font-mono-tech font-bold"
          >
            ⚠️ {w.label}: {w.total}/{w.min} —{" "}
            {w.pool === "BEANIES"
              ? t("est_form_beanies_min")
              : t("est_form_pool_min", { pool: w.label, min: w.min })}
          </div>
        ))}
        <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
          {form.items.length === 0 && (
            <div className="p-6 text-center text-sm text-[var(--text-secondary)]">
              {t("est_form_items_empty")} <b>{t("est_form_add_item_hint_bold")}</b>.
            </div>
          )}
          {form.items.map((it, i) => (
            <div key={i} className="p-3 flex items-center gap-3">
              {it.catalog_image_url && (
                <img
                  src={it.catalog_image_url}
                  alt=""
                  className="w-10 h-10 object-contain border border-[var(--border)] bg-white"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">
                  {it.description || it.product_code || t("est_form_item_unnamed")}
                </div>
                <div className="text-xs text-[var(--text-secondary)] truncate">
                  {[it.catalog_color, it.catalog_size].filter(Boolean).join(" · ")}
                  {it.commercial_group && (
                    <span className="ml-2 font-mono-tech">
                      · {it.commercial_group}
                      {it.commercial_tier ? ` @ ${it.commercial_tier}` : ""}
                    </span>
                  )}
                </div>
              </div>
              <input
                type="number"
                min={0}
                step="1"
                value={it.quantity ?? 0}
                onChange={(e) =>
                  updateItem(i, { quantity: parseFloat(e.target.value) || 0 })
                }
                className="w-16 border border-[var(--border)] px-2 py-1 text-sm text-right"
                data-testid={`estimate-form-item-qty-${i}`}
              />
              <div className="w-24 text-right text-sm">
                {money(it.unit_price)}
              </div>
              <div className="w-28 text-right text-sm font-bold">
                {money((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
              </div>
              {(it.is_custom || it.source_type === "custom") && (
                <button
                  type="button"
                  onClick={() => setCustomEditor({ open: true, index: i })}
                  data-testid={`estimate-form-item-edit-custom-${i}`}
                  className="text-xs font-mono-tech font-bold border border-[var(--border)] px-2 py-1 hover:bg-black hover:text-white"
                >
                  {t("est_form_item_edit_custom")}
                </button>
              )}
              <button
                type="button"
                onClick={() => removeItem(i)}
                data-testid={`estimate-form-item-remove-${i}`}
                className="text-xs text-[var(--alert-overdue,#c00)] hover:underline"
              >
                {t("est_form_item_remove")}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Nota del producto por línea (reutiliza customer_notes) */}
      {form.items.length > 0 && (
        <section className="space-y-2">
          {form.items.map((it, i) => (
            <div key={`note-${i}`} className="flex items-center gap-2">
              <div className="w-40 text-xs text-[var(--text-secondary)] truncate shrink-0">
                {it.description || it.product_code || t("est_form_item_unnamed")}
                {it.catalog_size ? ` · ${it.catalog_size}` : ""}
              </div>
              <input
                type="text"
                value={it.customer_notes || ""}
                onChange={(e) => updateItem(i, { customer_notes: e.target.value })}
                placeholder={t("est_form_item_note_ph")}
                data-testid={`estimate-form-item-note-${i}`}
                className="flex-1 border border-[var(--border)] px-2 py-1 text-xs"
              />
            </div>
          ))}
        </section>
      )}

      {/* Discount / Tax */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label={t("est_form_field_discount_type")}>
          <select
            data-testid="estimate-form-discount-type"
            value={form.discount_type}
            onChange={(e) => setField("discount_type", e.target.value)}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          >
            <option value="amount">{t("est_form_discount_amount")}</option>
            <option value="percent">{t("est_form_discount_percent")}</option>
          </select>
        </Field>
        <Field label={t("est_form_field_discount_value")}>
          <input
            data-testid="estimate-form-discount-value"
            type="number"
            min={0}
            step="0.01"
            value={form.discount_value}
            onChange={(e) =>
              setField("discount_value", parseFloat(e.target.value) || 0)
            }
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
        <Field label={t("est_form_field_tax")}>
          <label className="flex items-center gap-2 mt-2">
            <input
              data-testid="estimate-form-tax-exempt"
              type="checkbox"
              checked={form.tax_exempt}
              onChange={(e) => setField("tax_exempt", e.target.checked)}
            />
            <span className="text-sm">{t("est_form_tax_exempt_label")}</span>
          </label>
        </Field>
        {/* GP 1.0 · Sprint 5 · Issue #1 — "Depósito recibido" removido de la
            presentación del Estimate. El campo legacy se preserva en el
            estado y en el backend para no romper datos existentes. */}
      </section>

      {/* Notes */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("est_form_field_customer_notes")}>
          <textarea
            data-testid="estimate-form-customer-notes"
            value={form.customer_notes}
            onChange={(e) => setField("customer_notes", e.target.value)}
            rows={4}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
        <Field label={t("est_form_field_internal_notes")}>
          <textarea
            data-testid="estimate-form-internal-notes"
            value={form.internal_notes}
            onChange={(e) => setField("internal_notes", e.target.value)}
            rows={4}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm bg-yellow-50"
          />
        </Field>
      </section>

      {/* Follow-up notes */}
      {form.follow_up_date && (
        <section>
          <Field label={t("est_form_field_followup_notes")}>
            <textarea
              data-testid="estimate-form-follow-up-notes"
              value={form.follow_up_notes}
              onChange={(e) => setField("follow_up_notes", e.target.value)}
              rows={2}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
            />
          </Field>
        </section>
      )}

      {/* Commercial summary */}
      <section
        data-testid="estimate-form-summary"
        className="bg-black text-white p-5 space-y-2 font-mono-tech"
      >
        <div className="flex justify-between text-sm">
          <span>{t("est_form_summary_subtotal")}</span>
          <span data-testid="estimate-form-subtotal">{money(totals.subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>{t("est_form_summary_discount")}</span>
          <span>-{money(totals.discount_amount)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>{form.tax_exempt ? t("est_form_summary_tax_exempt") : t("est_form_summary_tax_rate", { pct: (form.sales_tax_rate * 100).toFixed(2) })}</span>
          <span>{money(totals.sales_tax_amount)}</span>
        </div>
        <div className="flex justify-between text-xl font-bold border-t border-white/20 pt-2">
          <span>{t("est_form_summary_total")}</span>
          <span data-testid="estimate-form-total">{money(totals.total_amount)}</span>
        </div>
      </section>

      <div className="flex justify-end gap-3 sticky bottom-0 bg-white pt-3 border-t border-[var(--border)]">
        <button
          type="button"
          onClick={onCancel}
          className="font-mono-tech text-xs border border-[var(--border)] px-6 py-3 hover:bg-black hover:text-white transition-colors"
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          data-testid="estimate-form-save"
          disabled={saving}
          onClick={submit}
          className="font-display font-bold text-sm bg-black text-white px-6 py-3 hover:bg-[var(--accent-magenta,#E5007D)] transition-colors disabled:opacity-40"
        >
          {saving ? t("saving_ellipsis") : initial?.id ? t("est_form_submit_edit") : t("est_form_submit_new")}
        </button>
      </div>

      {/* Add-product router */}
      <AddProductRouter
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(kind) => {
          setPickerOpen(false);
          if (kind?.kind === "internal_catalog") setCatalogOpen(true);
          else if (kind?.kind === "internal_custom_catalog") setCustomCatalogOpen(true);
          else if (kind?.kind === "manual_item") setManualOpen(true);
          else if (kind?.kind === "vinyl_calculator") setVinylOpen(true);
          else if (kind?.kind === "banner_calculator") setBannerOpen(true);
          else if (kind?.kind === "internal_service") setServiceOpen(true);
          else if (kind?.kind === "internal_custom") {
            // Open the full Custom Product editor (Feb 2026 correction)
            setCustomEditor({ open: true, index: null });
          }
        }}
      />
      {catalogOpen && (
        <CatalogPicker
          open={catalogOpen}
          onClose={() => setCatalogOpen(false)}
          onAdd={(items) => {
            addItem(items);
            setCatalogOpen(false);
          }}
        />
      )}
      {customCatalogOpen && (
        <ProductCatalogPickerModal
          open={customCatalogOpen}
          onClose={() => setCustomCatalogOpen(false)}
          onAdd={(item) => addItem(item)}
        />
      )}
      {manualOpen && (
        <ManualItemForm
          onClose={() => setManualOpen(false)}
          onAdd={(item) => {
            addItem(item);
            setManualOpen(false);
          }}
        />
      )}
      {vinylOpen && (
        <VinylCalculatorModal
          onClose={() => setVinylOpen(false)}
          onAdd={(item) => addItem(item)}
        />
      )}
      {bannerOpen && (
        <BannerCalculatorModal
          onClose={() => setBannerOpen(false)}
          onAdd={(item) => addItem(item)}
        />
      )}
      {serviceOpen && (
        <ServiceItemForm
          open={serviceOpen}
          onClose={() => setServiceOpen(false)}
          onAdd={(item) => {
            addItem(item);
            setServiceOpen(false);
          }}
        />
      )}
      <CustomProductEditor
        open={customEditor.open}
        initial={customEditor.index != null ? form.items[customEditor.index] : null}
        showCost={!!(user && (user.kind === "admin"))}
        onCancel={() => setCustomEditor({ open: false, index: null })}
        onSave={(item) => {
          if (customEditor.index != null) {
            updateItem(customEditor.index, item);
          } else {
            addItem(item);
          }
          setCustomEditor({ open: false, index: null });
        }}
      />
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mb-1">
        {label.toUpperCase()}
      </div>
      {children}
    </label>
  );
}

/**
 * FASE 2 · Jun 2026 — ManualItemForm
 * Formulario simple para agregar un item manual (no se guarda en catálogo).
 * Campos: Descripción, Cantidad, Precio unitario, Nota del producto.
 * Emite un EstimateItem-compatible vía onAdd → addItem() existente.
 */
function ManualItemForm({ onClose, onAdd }) {
  const { t } = useI18n();
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");

  const submit = () => {
    if (!desc.trim()) {
      toast.error(t("manual_item_required_desc"));
      return;
    }
    onAdd({
      product_code: "",
      description: desc.trim(),
      quantity: Math.max(1, Number(qty) || 1),
      unit_price: Math.max(0, Number(price) || 0),
      customer_notes: note.trim(),
    });
  };

  const inputCls =
    "w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black";

  return (
    <div
      data-testid="manual-item-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        data-testid="manual-item-modal"
        className="bg-white w-full max-w-lg border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-black flex items-start justify-between gap-3">
          <div>
            <div className="label-overline">GP / ADD PRODUCT</div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1">
              {t("manual_item_form_title")}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {t("manual_item_form_subtitle")}
            </p>
          </div>
          <button
            type="button"
            data-testid="manual-item-close"
            onClick={onClose}
            className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
          >
            ✕
          </button>
        </div>
        <div className="p-6 space-y-4">
          <Field label={t("manual_item_description")}>
            <input
              data-testid="manual-item-description"
              className={inputCls}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder={t("manual_item_description_ph")}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("manual_item_quantity")}>
              <input
                data-testid="manual-item-quantity"
                type="number"
                min="1"
                step="1"
                className={inputCls}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
            </Field>
            <Field label={t("manual_item_price")}>
              <input
                data-testid="manual-item-price"
                type="number"
                min="0"
                step="0.01"
                className={inputCls}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
              />
            </Field>
          </div>
          <Field label={t("manual_item_note")}>
            <textarea
              data-testid="manual-item-note"
              rows={2}
              className={inputCls}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("manual_item_note_ph")}
            />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end gap-3">
          <button
            type="button"
            data-testid="manual-item-cancel"
            onClick={onClose}
            className="font-mono-tech text-xs border border-[var(--border)] px-6 py-3 hover:bg-black hover:text-white transition-colors"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            data-testid="manual-item-add"
            onClick={submit}
            className="font-display font-bold text-sm bg-black text-white px-6 py-3 hover:bg-[var(--accent-magenta,#E5007D)] transition-colors"
          >
            {t("manual_item_add")}
          </button>
        </div>
      </div>
    </div>
  );
}
